import {
  Form,
  FormRow,
  FormSection,
  Loader,
  useForm,
  Dialog,
} from "@dbn/omnichannel-design-system-g4p";
import { option, taskEither } from "fp-ts";
import { pipe, constNull, constant } from "fp-ts/function";
import { Option } from "fp-ts/Option";
import { RuntimeLocaleKey, useFormatMessage } from "../../intl";
import { DropdownField } from "../../Common/DropdownField/DropdownField";
import { useValidators } from "../../Common/useValidators";
import { useQuery, useCommand } from "../../useAPI";
import * as api from "../api";
import * as remoteData from "../../RemoteData";
import { optionFromListKey, optionsFromList } from "./utils";
import { useMemo } from "react";
import { BankersDropdown } from "./BankersDropdown";

type BranchData = {
  branchCode: string;
  bankerId: string;
};

type Props = {
  onDismiss: () => void;
  onSubmit: () => void;
  initialValues: Option<BranchData>;
};

export function ApplicationBranchDialog(props: Props) {
  const formatMessage = useFormatMessage();
  const { defined } = useValidators();

  const [branchesData] = useQuery(api.getBranches);
  const savePreferredBanker = useCommand(api.savePreferredBanker);

  const { fieldProps, handleSubmit, values, setValues } = useForm(
    {
      initialValues: pipe(
        props.initialValues,
        option.fold(
          constant({
            branchCode: option.none,
            bankerId: option.none,
          }),
          initialValues => ({
            branchCode: option.some(initialValues.branchCode),
            bankerId: option.some(initialValues.bankerId),
          })
        )
      ),
      fieldValidators: _ => ({
        branchCode: defined(),
        bankerId: defined(),
      }),
    },
    {
      onSubmit: ({ branchCode, bankerId }) =>
        pipe({ branchCode, bankerId }, values =>
          pipe(
            savePreferredBanker(values),
            taskEither.chain(() => taskEither.fromIO(props.onSubmit))
          )
        ),
    }
  );

  const branchesDropdown = useMemo(
    () =>
      pipe(
        branchesData,
        remoteData.fold(
          constant(
            <Loader
              ariaLabelMessage={formatMessage(
                "generic.loading.alt" as RuntimeLocaleKey
              )}
            />
          ),
          constNull,
          ({ branches }) => (
            <DropdownField
              {...fieldProps("branchCode")}
              label={formatMessage("Mortgage.Dashboard.Overview.branch.branch")}
              placeholder={formatMessage(
                "Mortgage.Dashboard.Overview.branch.branch"
              )}
              options={optionsFromList(branches)}
              value={pipe(values.branchCode, optionFromListKey(branches))}
              onChange={value =>
                pipe(
                  value,
                  option.map(({ value }) => value),
                  branchCode =>
                    setValues({
                      branchCode,
                      bankerId: option.none,
                    })
                )
              }
              searchable
              clearable={false}
            />
          )
        )
      ),
    [branchesData, values.branchCode]
  );

  return (
    <Dialog
      ariaLabelMessageForCloseIcon={formatMessage(
        "Modal.closeButton" as RuntimeLocaleKey
      )}
      variant="left"
      title={formatMessage(
        option.isSome(props.initialValues)
          ? "Mortgage.Dashboard.Overview.branch.editBranchOrBanker"
          : "Mortgage.Dashboard.Overview.branch.chooseYourBranch"
      )}
      onDismiss={option.some(props.onDismiss)}
      actions={[
        {
          variant: "secondary",
          action: props.onDismiss,
          label: formatMessage("Cancel"),
        },
        {
          variant: "primary",
          action: handleSubmit,
          label: formatMessage("Save"),
        },
      ]}
      size="medium"
    >
      <Form>
        <FormSection>
          <FormRow type="full">{branchesDropdown}</FormRow>
          {pipe(
            values.branchCode,
            option.map(branchCode => (
              <FormRow type="full">
                <BankersDropdown
                  branchCode={branchCode}
                  {...fieldProps("bankerId")}
                  label={formatMessage(
                    "Mortgage.Dashboard.Overview.branch.banker"
                  )}
                  placeholder={formatMessage(
                    "Mortgage.Dashboard.Overview.branch.banker"
                  )}
                  searchable
                  clearable={false}
                  value={values.bankerId}
                  onChange={bankerId => setValues({ bankerId })}
                />
              </FormRow>
            )),
            option.toNullable
          )}
        </FormSection>
      </Form>
    </Dialog>
  );
}
